import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useDataHelper } from 'utils/dataHelper';
import { LoaderContext } from 'context/LoaderContextProvider';
import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { breakoutRoomModel } from 'entities/breakoutRoomModel';

import PageHeader from '../components/PageHeader';
import { SdmSpacer, SdmTextButton } from 'components';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Grid, Tooltip } from '@material-ui/core';
import { SubHeader, Body } from './Details/DetailsComponents';
import AcceptInvitationDialog from './AcceptInvitationDialog';
import DeclineInvitationDialog from './DeclineInvitationDialog';
import { cdnUrl } from 'utils/constants';

const Details = ({ user }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.detailsPage(intl);
  const dialogsTransalation = translations.personalArea(intl).userPage;
  const { isLoaderActive, setLoaderActive } = useContext(LoaderContext);
  const history = useHistory();
  const {
    generateIcalBlob,
    getTooltipText,
    checkEnterButtonIsDisabled,
    joinBr,
    joinChat,
    checkPassedDate,
  } = useDataHelper();
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const mediaTheme = useTheme();
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  // States
  const [fetchedInvitation, setfetchedInvitation] = useState();
  const [acceptInviteDialog, setAcceptInviteDialog] = useState({
    isOpen: false,
  });
  const [declineInviteDialog, setDeclineInviteDialog] = useState({
    isOpen: false,
  });
  // Effects
  useEffect(() => {
    fetchBr();
  }, []);
  // Functions
  const fetchBr = () => {
    showLoader();
    if (history.location.state) {
      setfetchedInvitation(history.location.state);
      hideLoader();
    } else {
      history.push('/', null);
    }
  };

  const fetchImage = (img) => {
    if (img) return `${cdnUrl}/${img}`;
    else return '/themes/custom/sdm/images/mail/breakout-room-img.jpg';
  };

  const handleAddCalendar = () => {
    const iCalBlob = generateIcalBlob({
      isChatType: fetchedInvitation.type === breakoutRoomModel.enums.types.CHAT,
      info: {
        brandName: fetchedInvitation.brand.name,
        date: fetchedInvitation.date,
        start: fetchedInvitation.start,
        end: fetchedInvitation.end,
      },
    });
    const customCreateObjectURL = (file) => {
      try {
        if (window.webkitURL) {
          return window.webkitURL.createObjectURL(file);
        } else if (window.URL && window.URL.createObjectURL) {
          return window.URL.createObjectURL(file);
        } else {
          return null;
        }
      } catch (e) {
        return null;
      }
    };

    const csvURL = customCreateObjectURL(iCalBlob);
    if (csvURL) {
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', `appointment.ics`);
      tempLink.click();
    } else {
      console.log('ERROR::');
    }
  };

  const handleAccept = async () => {
    showLoader();
    setAcceptInviteDialog({ isOpen: true });
    const input = {
      id: fetchedInvitation.id,
      status: breakoutRoomInviteModel.enums.status.ACCEPTED,
    };
    await breakoutRoomInviteModel.gql.update(input);
    const updatedInvitation = {
      ...fetchedInvitation,
      status: breakoutRoomInviteModel.enums.status.ACCEPTED,
    };
    setfetchedInvitation(updatedInvitation);
    hideLoader();
  };

  const handleDecline = async (data) => {
    showLoader();
    const input = {
      id: fetchedInvitation.id,
      note: data.note,
      status: breakoutRoomInviteModel.enums.status.REJECTED,
    };
    await breakoutRoomInviteModel.gql.update(input);
    const updatedInvitation = {
      ...fetchedInvitation,
      status: breakoutRoomInviteModel.enums.status.REJECTED,
    };
    setfetchedInvitation(updatedInvitation);
    hideLoader();
  };

  const handleEnter = () => {
    if (isChatType()) {
      joinChat(fetchedInvitation.breakoutRoomId, user?.externalId);
    } else {
      joinBr(fetchedInvitation.breakoutRoomId, user?.email);
    }
  };

  const checkStatus = (status) => {
    return status === breakoutRoomInviteModel.enums.status.CANCELED ||
      status === breakoutRoomInviteModel.enums.status.REJECTED
      ? true
      : false;
  };

  const checkActionButtonIsDisabled = () => {
    if (
      fetchedInvitation.status === breakoutRoomInviteModel.enums.status.ACCEPTED
    ) {
      return checkEnterButtonIsDisabled(fetchedInvitation);
    }
    return false;
  };

  // Renders
  const renderAcceptInvitationDialog = () => {
    if (!acceptInviteDialog.isOpen) return null;
    return (
      <AcceptInvitationDialog
        onClose={() => {
          setAcceptInviteDialog({ isOpen: false });
        }}
        i18n={dialogsTransalation.acceptDialog}
      />
    );
  };

  const renderDeleteInviteDialog = () => {
    if (!declineInviteDialog.isOpen) return null;
    return (
      <DeclineInvitationDialog
        onAgree={handleDecline}
        onClose={() => {
          setDeclineInviteDialog({ isOpen: false });
        }}
        i18n={dialogsTransalation.declineDialog}
        data={declineInviteDialog.data}
      />
    );
  };

  const isChatType = () => {
    return fetchedInvitation.type === breakoutRoomModel.enums.types.CHAT;
  };

  return (
    <div style={{ width: '80%', margin: '0 auto', maxWidth: 1250 }}>
      <Grid container>
        <div style={{ width: '100%' }}>
          {renderAcceptInvitationDialog()}
          {renderDeleteInviteDialog()}
          {!isLoaderActive && fetchedInvitation && (
            <>
              <PageHeader
                i18n={i18n}
                hasBackBtn
                isBrandPage={false}
                hasCustomTitle={fetchedInvitation.name}
                onBack={() => history.push('/', null)}
              />
              <Grid container>
                <SubHeader
                  fetchedBr={fetchedInvitation}
                  i18n={i18n}
                  onAddCalendar={handleAddCalendar}
                  intl={intl}
                />
                <Grid item xs={12}>
                  <img
                    src={fetchImage(fetchedInvitation.coverImage)}
                    alt="image"
                    style={{
                      width: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain',
                    }}
                  />
                </Grid>
                <SdmSpacer height={40} />
                <Grid container>
                  <Body
                    fetchedBr={fetchedInvitation}
                    i18n={i18n}
                    isXsDown={isSmDown}
                  />
                  <Grid item md={5} sm={12} xs={12}>
                    {checkPassedDate(fetchedInvitation?.end) ? null : (
                      <Grid
                        container
                        alignItems={'center'}
                        justify={
                          fetchedInvitation.status ===
                            breakoutRoomInviteModel.enums.status.PENDING ||
                          fetchedInvitation.status ===
                            breakoutRoomInviteModel.enums.status.ACCEPTED
                            ? isSmDown
                              ? 'center'
                              : 'flex-start'
                            : 'flex-start'
                        }
                      >
                        <SdmTextButton
                          variant={'red'}
                          disabled={checkStatus(fetchedInvitation.status)}
                          text={
                            fetchedInvitation.status ===
                              breakoutRoomInviteModel.enums.status.CANCELED ||
                            fetchedInvitation.status ===
                              breakoutRoomInviteModel.enums.status.REJECTED
                              ? i18n.buttons[fetchedInvitation.status]
                              : i18n.buttons.decline
                          }
                          textVariant={'h4Regular'}
                          onClick={() =>
                            setDeclineInviteDialog({
                              isOpen: true,
                              data: fetchedInvitation,
                            })
                          }
                          style={{
                            minWidth: isXsDown ? 110 : 192,
                            height: 46,
                          }}
                        />
                        {fetchedInvitation.status !==
                          breakoutRoomInviteModel.enums.status.CANCELED &&
                        fetchedInvitation.status !==
                          breakoutRoomInviteModel.enums.status.REJECTED ? (
                          <Tooltip
                            title={getTooltipText(
                              fetchedInvitation,
                              isChatType()
                            )}
                          >
                            <div>
                              <SdmTextButton
                                variant={'redFill'}
                                text={
                                  fetchedInvitation.status ===
                                  breakoutRoomInviteModel.enums.status.PENDING
                                    ? i18n.buttons.accept
                                    : i18n.buttons.enter
                                }
                                textVariant={'h4Regular'}
                                onClick={
                                  fetchedInvitation.status ===
                                  breakoutRoomInviteModel.enums.status.PENDING
                                    ? handleAccept
                                    : handleEnter
                                }
                                disabled={checkActionButtonIsDisabled()}
                                style={{
                                  minWidth: isXsDown ? 110 : 192,
                                  height: 46,
                                }}
                              />
                            </div>
                          </Tooltip>
                        ) : null}
                      </Grid>
                    )}
                  </Grid>
                  <Grid item md={7} />
                  <SdmSpacer height={100} />
                </Grid>
              </Grid>
            </>
          )}
        </div>
      </Grid>
    </div>
  );
};

export default Details;
